/**
 * File: components/ServiceProviderDashboard.jsx
 * ==============================================
 * Simplified dashboard for service providers
 * Shows ONLY assigned tasks, no property details or financials
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const PRIORITY_COLORS = {
  high: 'text-red-600',
  medium: 'text-yellow-600',
  low: 'text-green-600'
};

export default function ServiceProviderDashboard() {
  const { getToken } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    setError('');

    try {
      const token = getToken();

      // Service providers need to load tasks from properties they have access to
      // For now, we'll use a dedicated endpoint
      const response = await fetch('/api/tasks/assigned', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 404) {
        // Endpoint might not exist yet, show empty state
        setTasks([]);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load tasks');
      }

      const data = await response.json();
      setTasks(data);
    } catch (err) {
      // If the endpoint doesn't exist, show a friendly message
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, propertyId, newStatus) => {
    setUpdating(true);

    try {
      const token = getToken();
      const response = await fetch(`/api/tasks/${propertyId}/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update task');
      }

      // Refresh tasks
      loadTasks();
      setSelectedTask(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading your tasks...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">My Assigned Tasks</h1>
          <p className="text-gray-600 mt-1">Tasks assigned to you by property owners</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {tasks.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No tasks assigned yet</h3>
            <p className="text-gray-500">When a property owner assigns tasks to you, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedTask(task)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {task.priority && (
                        <span className={`text-xs font-medium ${PRIORITY_COLORS[task.priority.toLowerCase()] || 'text-gray-600'}`}>
                          {task.priority}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[task.status] || 'bg-gray-100 text-gray-800'}`}>
                        {STATUS_LABELS[task.status] || task.status}
                      </span>
                    </div>

                    <h3 className="text-lg font-medium text-gray-900">{task.title}</h3>

                    {task.description && (
                      <p className="text-gray-600 mt-1 text-sm line-clamp-2">{task.description}</p>
                    )}

                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                      {task.propertyName && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          {task.propertyName}
                        </span>
                      )}
                      {task.dueDate && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Task Details</h2>
              <button
                onClick={() => setSelectedTask(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedTask.status] || 'bg-gray-100 text-gray-800'}`}>
                    {STATUS_LABELS[selectedTask.status] || selectedTask.status}
                  </span>
                  {selectedTask.priority && (
                    <span className={`text-xs font-medium ${PRIORITY_COLORS[selectedTask.priority.toLowerCase()] || 'text-gray-600'}`}>
                      {selectedTask.priority} Priority
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-medium text-gray-900">{selectedTask.title}</h3>
              </div>

              {selectedTask.description && (
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Description</div>
                  <p className="text-gray-700">{selectedTask.description}</p>
                </div>
              )}

              {selectedTask.propertyName && (
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Property</div>
                  <p className="text-gray-700">{selectedTask.propertyName}</p>
                </div>
              )}

              {selectedTask.dueDate && (
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Due Date</div>
                  <p className="text-gray-700">{new Date(selectedTask.dueDate).toLocaleDateString()}</p>
                </div>
              )}

              {selectedTask.notes && (
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Notes</div>
                  <p className="text-gray-700">{selectedTask.notes}</p>
                </div>
              )}

              {/* Status Update */}
              <div className="pt-4 border-t">
                <div className="text-sm font-medium text-gray-500 mb-3">Update Status</div>
                <div className="flex gap-2">
                  {['pending', 'in_progress', 'completed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateTaskStatus(selectedTask.id, selectedTask.propertyId, status)}
                      disabled={updating || selectedTask.status === status}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedTask.status === status
                          ? 'bg-indigo-100 text-indigo-800 cursor-default'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } disabled:opacity-50`}
                    >
                      {STATUS_LABELS[status]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
